<template>
  <div class="row">
    <div class="col-12">
      <div class="rect rect--highlight mb-4">
        <StatisitcsTimeSelector
          :lastValues="lastValues"
          :isEnabled="isEnabled"
          @update:currentDate="currentDate = $event"
          @update:selectedPeriod="selectedPeriod = $event"
        />
      </div>
      <AnalyticsTable :config="llmStatisticsConfig" :cells="getLLMStats"></AnalyticsTable>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { LLMObject } from '../../../../../../common/types/llm-type'
import Autocomplete from '@/components/helpers/Autocomplete.vue'
import Dropdown from '@/components/helpers/Dropdown.vue'
import Tooltip from '@/components/helpers/Tooltip.vue'
import { AnalyticsTableConfig } from '@/models/analytics-table.interface'
import { fromMillisecondsToSeconds } from '@/components/filters/number.filters'
import { LlmsService } from '@/services/bots/llms.services'
import AnalyticsTable from '@/components/analytics/AnalyticsTable.vue'
import StatisitcsTimeSelector from '@/components/helpers/StatisticsTimeSelector.vue'
import { formatLocaleDate } from '@common/helpers/date.helper'

@Component({ components: { Autocomplete, Dropdown, Tooltip, AnalyticsTable, StatisitcsTimeSelector } })
export default class LLMStatistics extends Vue {
  @Prop() LLM: LLMObject
  @Prop() variables: string[]

  private llmService: LlmsService = new LlmsService()
  private llmData: any = {}
  private currentDate = new Date()
  private selectedPeriod: 'daily' | 'monthly' = 'monthly'
  private isEnabled = true

  get lastValues() {
    // From this date, llm statistics are available
    return this.getMonthAndYearFromDate(new Date(this.LLM.dateCreated || '2025-05-01'))
  }

  private getMonthAndYearFromDate(date: Date) {
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    return { month, year }
  }

  async mounted() {
    this.refresh()
  }

  @Watch('currentDate')
  @Watch('selectedPeriod')
  async refresh() {
    this.llmData = await this.llmService.getMetricsByLlmId(this.LLM.llmId, this.currentDate)
  }

  async setCurrentDate(date: Date) {
    this.currentDate = date
  }

  private llmStatisticsConfig: AnalyticsTableConfig = {
    name: 'LLM Metrics Detailed',
    headerCells: ['Metric', 'Average', 'Maximum'],
    columnSettings: [
      { sortType: 'string' },
      { sortType: 'number' },
      {
        sortType: 'number'
      }
    ]
  }

  @Watch('llmData')
  get getLLMStats() {
    let llmData = this.llmData?.[0] || {}
    if (this.selectedPeriod == 'daily' && llmData?.dailyMetrics) {
      llmData = llmData.dailyMetrics[formatLocaleDate(this.currentDate)]
    }
    return [
      [
        'Input guardrails time',
        fromMillisecondsToSeconds(llmData?.averageInputGuardrailsTime || 0),
        fromMillisecondsToSeconds(llmData?.maxInputGuardrailsTime || 0)
      ],
      [
        'Input guardrails time (blocking response)',
        fromMillisecondsToSeconds(llmData?.averageInputGuardrailsBlockingTime || 0),
        fromMillisecondsToSeconds(llmData?.maxInputGuardrailsBlockingTime || 0)
      ],
      [
        'Output guardrails time',
        fromMillisecondsToSeconds(llmData?.averageOutputGuardrailsTime || 0),
        fromMillisecondsToSeconds(llmData?.maxOutputGuardrailsTime || 0)
      ],
      [
        'Output guardrails time (blocking response)',
        fromMillisecondsToSeconds(llmData?.averageOutputGuardrailsBlockingTime || 0),
        fromMillisecondsToSeconds(llmData?.maxOutputGuardrailsBlockingTime || 0)
      ],
      [
        'First sentence generation time',
        fromMillisecondsToSeconds(llmData?.averageFirstSentenceTime || 0),
        fromMillisecondsToSeconds(llmData?.maxFirstSentenceTime || 0)
      ],
      ['RAG filter time', fromMillisecondsToSeconds(llmData?.averageRagFilterTime || 0), fromMillisecondsToSeconds(llmData?.maxRagFilterTime || 0)],
      ['RAG chain time', fromMillisecondsToSeconds(llmData?.averageGetRagChainTime || 0), fromMillisecondsToSeconds(llmData?.maxGetRagChainTime || 0)],
      ['Chain time', fromMillisecondsToSeconds(llmData?.averageTotalChainTime || 0), fromMillisecondsToSeconds(llmData?.maxTotalChainTime || 0)],
      [
        'LLM section time',
        fromMillisecondsToSeconds(llmData?.averageLlmExecutionTotalTime || 0),
        fromMillisecondsToSeconds(llmData?.maxLlmExecutionTotalTime || 0)
      ],
      [
        'LLM first content response time',
        fromMillisecondsToSeconds(llmData?.averageResponseTime || 0),
        fromMillisecondsToSeconds(llmData?.maxResponseTime || 0)
      ],
      [
        'LLM block total execution time',
        fromMillisecondsToSeconds(llmData?.averageLlmBlockTotalTime || 0),
        fromMillisecondsToSeconds(llmData?.maxLlmBlockTotalTime || 0)
      ],
      { text: 'Model interactions', formula: llmData?.interactions || 0 }
    ]
  }

  get datasetName() {
    return this.LLM.datasetName
  }

  set datasetName(value: string) {
    this.LLM.datasetName = value
  }

  get datasetId() {
    return this.LLM.datasetId
  }

  set datasetId(value: string) {
    this.LLM.datasetId = value
  }

  evaluateLLM() {
    this.$emit('evaluateLLM')
  }

  togglePeriod(type: 'daily' | 'monthly') {
    this.selectedPeriod = type
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/scss/variables';

.description {
  font-size: 14px;
  margin-bottom: 15px;
}

.mt {
  margin-top: 20px;
}

.rect {
  &--highlight {
    color: white;
    width: 100%;
    height: auto;
    padding: 30px 30px 50px;
    background-color: $default-dark;
    border-radius: 10px;
  }
}
</style>
